import React, {FC} from "react"
import {useTranslation} from "react-i18next"

import {IconButton} from "../../components/Button.tsx"
import {ProfileImage} from "../../components/ProfileImage.tsx"
import {Table as LayoutTable} from "../../components/Table/Table.tsx"
import {getColumnsFromMeta} from "../../components/Table/utils/columns.ts"
import {TColumnsMetaWithEmpty} from "../../components/Table/utils/shared.ts"
import {useLSTableColumnsState} from "../../components/Table/utils/useLSTableColumnsState.tsx"
import {Tooltip} from "../../components/Tooltip.tsx"
import {useUserSettingsOrLogout} from "../../queries/user.ts"
import {
  ASaasCompanySalesIntelligenceDashboardSalesCycle,
  ASaasCompanyUser,
  ASaasSalesPerson,
  ASalesIntelligenceAssignmentStatistics,
  AUsersCompanyUser,
  AUsersSalesPerson,
  AUserTypes,
} from "../../services/types.generated.ts"
import {getFullName} from "../../utils"
import {formatTableValue, TAssignmentStatsColumn} from "./utils.ts"

const tableId = "saas/dashboard"

export const Table: FC<{salesCycle: ASaasCompanySalesIntelligenceDashboardSalesCycle}> = ({salesCycle}) => {
  const {t} = useTranslation()
  const {user} = useUserSettingsOrLogout()

  const rows = salesCycle.assignment_statistics
  const stats = salesCycle.statistics

  const columnsMeta = React.useMemo<
    TColumnsMetaWithEmpty<TAssignmentStatsColumn, ASalesIntelligenceAssignmentStatistics>
  >(() => {
    const commonHeaderProps = {
      align: "left" as const,
      headerCellClassName: "border-b border-t",
    }

    const highlightRow =
      (
        data: ASalesIntelligenceAssignmentStatistics[],
        user: AUsersCompanyUser | AUsersSalesPerson | ASaasCompanyUser | ASaasSalesPerson
      ) =>
      ({rowIndex}: {rowIndex: number}) => {
        const thisRow = data.at(rowIndex)

        return thisRow?.sales_person?.id === user.id && user.type === AUserTypes.SaasSalesPerson
          ? "border-t border-b border-[#E0E7FF] bg-[#F7F9FF]"
          : ""
      }

    return [
      {
        column: "rowindex",
        size: "min-content",
        align: "center",
        headerCellClassName: "border-b border-t",
        cellClassName: highlightRow(rows ?? [], user),
        HeaderCellValue: () => "#",
        CellValue: ({rowIndex}) => (rowIndex ?? 0) + 1,
      },
      {
        column: "salesperson",
        size: "max-content",
        headerCellClassName: "border-b border-t",
        cellClassName: highlightRow(rows ?? [], user),
        HeaderCellValue: () => t("Dashboard_SaaS_Table_SalesReps"),
        CellValue: ({row}) =>
          row.sales_person ? (
            <div title={getFullName(row.sales_person)} className={"flex items-center gap-2"}>
              <ProfileImage
                className={"size-6 shrink-0"}
                src={row.sales_person.profile_picture_thumbnail_url ?? undefined}
              />
              <div className={"overflow-hidden font-medium text-ellipsis"}>{getFullName(row.sales_person)}</div>
            </div>
          ) : null,
      },
      {
        column: "active deals",
        cellClassName: highlightRow(rows ?? [], user),
        ...commonHeaderProps,
        HeaderCellValue: () => (
          <TableHeaderWithBadge
            label={t("Dashboard_SaaS_Table_ActiveDeals")}
            badgeValue={formatTableValue(stats?.active_deals)}
          />
        ),
        CellValue: ({row}) => {
          return (
            <Tooltip
              buttonNode={<IconButton noEdges>{formatTableValue(row?.active_deals)}</IconButton>}
              openDelay={200}
            >
              <div className={"w-28 rounded-lg bg-gray-900/90 px-2 py-2 text-sm text-cr-white"}>
                <span className={"text-xs font-bold"}>{t("Dashboard_SaaS_Table_Active")}: </span>
                <span className={"text-xs font-normal"}>{formatTableValue(row?.active_deals)}</span>
                <br />
                <span className={"text-xs font-bold"}>{t("Dashboard_SaaS_Table_Total")}: </span>
                <span className={"text-xs font-normal"}>{formatTableValue(row?.deals)}</span>
              </div>
            </Tooltip>
          )
        },
      },
      {
        column: "activities",
        cellClassName: highlightRow(rows ?? [], user),
        ...commonHeaderProps,
        HeaderCellValue: () => (
          <TableHeaderWithBadge
            label={t("Dashboard_SaaS_Table_Activities")}
            badgeValue={formatTableValue(stats?.activities)}
          />
        ),
        CellValue: ({row}) => formatTableValue(row.activities),
      },
      {
        column: "activities to won",
        cellClassName: highlightRow(rows ?? [], user),
        ...commonHeaderProps,
        HeaderCellValue: () => (
          <TableHeaderWithBadge
            label={t("Dashboard_SaaS_Table_ActivitiesToWon")}
            badgeValue={formatTableValue(stats?.activities_to_won_average)}
          />
        ),
        CellValue: ({row}) => formatTableValue(row.activities_to_won),
      },
      {
        column: "win / lost",
        cellClassName: highlightRow(rows ?? [], user),
        ...commonHeaderProps,
        HeaderCellValue: () => (
          <TableHeaderWithBadge
            label={t("Dashboard_SaaS_Table_DealsWonLost")}
            badgeValue={`${formatTableValue(stats?.won_deals_average)}/${formatTableValue(stats?.lost_deals_average)}`}
          />
        ),
        CellValue: ({row}) => (
          <>
            <span className={"font-bold"}>{formatTableValue(row.won_deals)}</span>/{formatTableValue(row.lost_deals)}
          </>
        ),
      },
      {
        column: "time to close",
        cellClassName: highlightRow(rows ?? [], user),
        ...commonHeaderProps,
        HeaderCellValue: () => (
          <TableHeaderWithBadge
            label={t("Dashboard_SaaS_Table_TimeToClose")}
            badgeValue={formatTableValue(stats?.time_to_close, "d")}
          />
        ),
        CellValue: ({row}) => formatTableValue(row.time_to_close, ` ${t("Dashboard_SaaS_Table_Days")}`),
      },
    ]
  }, [t, stats, rows, user])

  const columnsState = useLSTableColumnsState(tableId, {
    columnsOrder: getColumnsFromMeta(columnsMeta),
    pinnedColumn: null,
  })

  return columnsMeta && rows ? (
    <LayoutTable<TAssignmentStatsColumn, ASalesIntelligenceAssignmentStatistics>
      {...columnsState}
      className={"rounded-t-none border-0 bg-cr-white"}
      columnsMeta={columnsMeta}
      data={rows}
      noShadow={true}
    />
  ) : null
}

export const TableHeaderWithBadge: React.FC<{label: string; badgeValue?: string | number | null}> = ({
  label,
  badgeValue,
}) => (
  <>
    <div className={"justify-start font-['Sora'] text-blue-950"}>{label}</div>
    {badgeValue != null && (
      <div className={"flex items-center justify-center gap-2 rounded-2xl bg-slate-50 px-2 py-1"}>
        <div className={"justify-start font-['Sora'] text-[10px] font-normal text-blue-950"}>{badgeValue}</div>
      </div>
    )}
  </>
)
